export type TabularImportEntityType = 'provider' | 'product'

export type BranchStrategy = 'Concatenate' | 'PickFirstNonEmpty' | 'Format'

// =====================================================
// ExtractionNode Tree Types (matches backend JSON exactly)
// =====================================================

export interface ColumnLeaf {
  columnName: string
}

export interface BranchNode {
  strategy: BranchStrategy
  separator: string | null
  template: string | null
  children: ExtractionNode[]
}

export interface TransformNode {
  transformType: string
  input: ExtractionNode
  config: Record<string, unknown> | null
}

export type ExtractionNode = ColumnLeaf | BranchNode | TransformNode

// New mapping model (one per target field)
export interface FieldMappingDto {
  id: number
  targetFieldName: string
  extractionTree: ExtractionNode | null
  confidenceScore: number
  mappingReason: string
  isApplied: boolean
}

// Preview row — keys are targetFieldName values
export type PreviewRow = Record<string, string | null>

// Request type for PUT /mappings
export interface UpdateMappingsRequest {
  mappings: { id: number; isApplied: boolean }[]
}

// File format types
export type FileFormat = 'Csv' | 'Xlsx'

// New workflow statuses
export type ImportStatus =
  | 'Analyzing' // AI analyzing sample rows for mappings
  | 'AwaitingMappingApproval' // Waiting for user to approve/edit mappings
  | 'MappingApproved' // User approved, ready to process
  | 'Processing' // Background job running
  | 'AwaitingReview' // Processed, user reviewing results
  | 'DetectingDuplicates' // Background dedup job running
  | 'DeduplicationReview' // Dedup done, user reviewing dedup results
  | 'Importing' // Background import job running
  | 'Imported' // Providers created (terminal state)
  | 'Failed' // Error during any phase (terminal state)

export interface ExtractedData {
  rawValues: Record<string, string>
  extractedFields: Record<string, string | null>
}

export interface CategoryPreviewItem {
  name: string
  isNew: boolean
}

export interface ImportRecord {
  id: number
  rowNumber: number
  status: string
  processedAt: string | null
  validationErrors: string[]
  extractedData: string | null // JSON string that parses to ExtractedData
  isManuallyEdited?: boolean
  editedAt?: string | null
  categoryPreview?: CategoryPreviewItem[]
}

export interface ImportSession {
  sessionId: string
  entityType: string
  status: ImportStatus
  fileFormat: FileFormat
  fileName: string
  totalRows: number
  successfulRows: number
  failedRows: number
  createdAt: string
  completedAt: string | null
  errorMessage: string | null
  fieldMappings: FieldMappingDto[] | null
  previewRows: PreviewRow[] | null
  validRows: number
  incompleteRows: number
  categoryPlanJson: string | null
  existingCategoryNames?: Record<string, string> | null
  importedCreatedCount?: number
  importedUpdatedCount?: number
  importedFailedCount?: number
  importWarnings?: string[]
}

export interface PaginatedImportRecords {
  items: ImportRecord[]
  totalResult: number
  pagesCount: number
  itemsPerPage: number
  pageNumber: number
}

export interface TabularImportInitResponse {
  sessionId: string
  entityType: string
  message: string
  fileFormat: FileFormat
}

// Summary of an unfinished import session (for session list display)
export interface UnfinishedImportSession {
  sessionId: string
  entityType: string
  fileName: string
  fileFormat: FileFormat
  status: ImportStatus
  totalRows: number
  createdAt: string
}

// Helper function to parse extracted data from JSON string
export function parseExtractedData(json: string | null): ExtractedData | null {
  if (!json) return null
  try {
    return JSON.parse(json) as ExtractedData
  } catch {
    return null
  }
}

// Get display label for file format
export function getFileFormatLabel(format: FileFormat): string {
  switch (format) {
    case 'Csv':
      return 'CSV'
    case 'Xlsx':
      return 'Excel'
    default:
      return format
  }
}

// =====================================================
// Request/Response Types for Tabular Import
// =====================================================

export interface UpdateRecordRequest {
  extractedFields: Record<string, string>
  resolvedIds?: Record<string, number | null>
}

export interface StartImportRequest {
  rowNumbersToImport?: number[]
}

export interface StartImportResponse {
  message: string
}

export interface TabularImportProgressUpdate {
  sessionId: string
  fileFormat: FileFormat
  processedRows: number
  totalRows: number
  successCount: number
  failedCount: number
  lastUpdate: string
}

export interface ImportProgressUpdate {
  sessionId: string
  processedRows: number
  totalRows: number
  createdCount: number
  updatedCount: number
  failedCount: number
}

const PROCESSING_STATUSES: ImportStatus[] = [
  'Analyzing',
  'MappingApproved',
  'Processing',
  'DetectingDuplicates',
  'Importing',
]

export function isProcessingStatus(status: ImportStatus): boolean {
  return PROCESSING_STATUSES.includes(status)
}

// =====================================================
// Required Fields Validation
// =====================================================

export function hasRequiredFields(record: ImportRecord, requiredFields: readonly string[]): boolean {
  const extracted = parseExtractedData(record.extractedData)
  if (!extracted?.extractedFields) return false
  return requiredFields.every(field => {
    const value = extracted.extractedFields[field]
    return value !== null && value !== undefined && value.trim() !== ''
  })
}

export function getMissingRequiredFields(record: ImportRecord, requiredFields: readonly string[]): string[] {
  const extracted = parseExtractedData(record.extractedData)
  if (!extracted?.extractedFields) return [...requiredFields]
  return requiredFields.filter(field => {
    const value = extracted.extractedFields[field]
    return !value || value.trim() === ''
  })
}

export function extractedFieldsAreValid(
  extractedFields: Record<string, string>,
  requiredFields: readonly string[],
): boolean {
  return requiredFields.every(field => {
    const value = extractedFields[field]
    return value !== undefined && value.trim() !== ''
  })
}

// =====================================================
// Deduplication Types
// =====================================================

export interface DeduplicationSummary {
  status: ImportStatus
  processedRows: number
  totalRows: number
  confirmedCount: number
  newCount: number
  needsReviewCount: number
  skippedCount: number
}

export interface DeduplicationProgressUpdate {
  sessionId: string
  processedRows: number
  totalRows: number
  matchedCount: number
  newCount: number
  needsReviewCount: number
}

export interface PaginatedDeduplicationResults {
  items: DeduplicationResult[]
  totalCount: number
  page: number
  pageSize: number
}

/**
 * Per-field merge decision used in the dedup-update flow.
 *
 * - `'import'` — use the imported value (replaces existing, including for `Owners`).
 * - `'keep'`   — preserve the existing value as-is.
 * - `'add'`    — `Owners` only: union the imported owners with the existing set.
 */
export type FieldDecision = 'import' | 'keep' | 'add'

// matchedEntitySnapshot arrives as a JSON string from Newtonsoft serialization.
// Handle both string and already-parsed object.
export function parseSnapshot(
  snapshot: Record<string, string | null> | string | null,
): Record<string, string | null> | null {
  if (!snapshot) return null
  if (typeof snapshot === 'string') {
    try {
      return JSON.parse(snapshot) as Record<string, string | null>
    } catch {
      return null
    }
  }
  return snapshot
}

// fieldDecisions is stored as a JSON string in the DB and double-encoded by Newtonsoft.
// Handle both string and already-parsed object.
export function parseFieldDecisions(
  decisions: Record<string, FieldDecision> | string | null,
): Record<string, FieldDecision> | null {
  if (!decisions) return null
  if (typeof decisions === 'string') {
    try {
      return JSON.parse(decisions) as Record<string, FieldDecision>
    } catch {
      return null
    }
  }
  return decisions
}

export enum DeduplicationMatchSignal {
  None = 'None',
  WorldfavorId = 'WorldfavorId',
  CustomId = 'CustomId',
  RegistrationNumber = 'RegistrationNumber',
  Duns = 'Duns',
  NameCountry = 'NameCountry',
  Gtin = 'Gtin',
}

export enum DeduplicationUserAction {
  Pending = 'Pending',
  Update = 'Update',
  CreateNew = 'CreateNew',
  Skip = 'Skip',
}

export interface DeduplicationResult {
  id: number
  tabularImportRecordId: number
  matchedEntityId: number | undefined
  matchSignal: DeduplicationMatchSignal
  confidenceScore: number
  matchedEntitySnapshot: Record<string, string | null> | null
  userAction: DeduplicationUserAction
  assignedEntityId: number | undefined
  assignedEntitySnapshot: Record<string, string | null> | null
  fieldDecisions: Record<string, FieldDecision> | string | null
  record: ImportRecord | null
}

export interface DeduplicationResultWithRecord extends DeduplicationResult {
  importRecord: ImportRecord
}

// =====================================================
// File Format Helpers
// =====================================================

// Get accept attribute for file input
// Note: Using string format for compatibility with react-dropzone v11.0.1
// Can be changed to object format when upgrading to v11.0.2+
export const ACCEPTED_FILE_TYPES = '.csv,.xlsx'
